import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuestionnaire } from '../../lib/QuestionnaireContext';
import { useAuth } from '../../lib/AuthContext';
import { useTheme } from '../../lib/ThemeProvider';
import { Skeleton } from '../ui/Skeleton';
import logo from '../../logo.svg';

interface SubStep {
  title: string;
  description: string;
  stepIndex: number;
  conditional?: string;
}

interface Step {
  title: string;
  description: string;
  isSection?: boolean;
  stepIndex?: number; // For direct sections without substeps
  subSteps?: SubStep[];
  conditional?: string;
}

interface SidebarProps {
  steps: Step[];
  currentStep: number;
  onCloseMobile?: () => void; // Add this prop for mobile closing
}

const QuestionnaireSidebar: React.FC<SidebarProps> = ({ steps, currentStep, onCloseMobile }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentQuestionnaire, userQuestionnaires, loadQuestionnaire } = useQuestionnaire();
  const { logout, currentUser } = useAuth();
  const { theme, setTheme } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState<number[]>([]);
  const [questionnaireDropdownOpen, setQuestionnaireDropdownOpen] = useState(false);
  const settingsRef = useRef<HTMLDivElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);
  const questionnaireDropdownRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  
  // Initialize expanded sections based on current step
  useEffect(() => {
    if (currentStep >= 0) {
      // Find which section contains the current step
      const sectionIndex = steps.findIndex(section => 
        section.subSteps?.some(subStep => subStep.stepIndex === currentStep)
      );
      
      if (sectionIndex !== -1 && !expandedSections.includes(sectionIndex)) {
        setExpandedSections(prev => [...prev, sectionIndex]);
      }
    }
  }, [currentStep, steps, expandedSections]);
  
  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (settingsRef.current && !settingsRef.current.contains(event.target as Node)) {
        setSettingsOpen(false);
      }
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setProfileOpen(false);
      }
      if (questionnaireDropdownRef.current && !questionnaireDropdownRef.current.contains(event.target as Node)) {
        setQuestionnaireDropdownOpen(false);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  const handleNavigate = (index: number) => {
    if (!currentQuestionnaire) return;
    
    console.log(`Navigating to step: ${index}, Current step: ${currentStep}, Max step: ${currentQuestionnaire.currentStep}`);
    
    // Allow navigation to steps that have been visited
    if (currentQuestionnaire.currentStep !== undefined && index <= currentQuestionnaire.currentStep) {
      // Update the URL to navigate to the form with the specific step
      navigate(`/questionnaire/form/${currentQuestionnaire.id}?step=${index}`);
      setMobileOpen(false);
      // Close mobile sidebar if provided
      if (onCloseMobile) {
        onCloseMobile();
      }
    }
  };
  
  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out', error);
    }
  };

  const toggleSettings = () => {
    setSettingsOpen(!settingsOpen);
    setProfileOpen(false);
    setQuestionnaireDropdownOpen(false);
  };

  const toggleProfile = () => {
    setProfileOpen(!profileOpen);
    setSettingsOpen(false);
    setQuestionnaireDropdownOpen(false);
  };

  const toggleQuestionnaireDropdown = () => {
    setQuestionnaireDropdownOpen(!questionnaireDropdownOpen);
    setSettingsOpen(false);
    setProfileOpen(false);
  };

  const handleQuestionnaireSelect = async (questionnaireId: string) => {
    try {
      // Load the selected questionnaire
      await loadQuestionnaire(questionnaireId);
      
      // Navigate to the questionnaire form
      navigate(`/questionnaire/form/${questionnaireId}?step=0`);
      
      // Close the dropdown
      setQuestionnaireDropdownOpen(false);
      setMobileOpen(false);
      
      // Close mobile sidebar if provided
      if (onCloseMobile) {
        onCloseMobile();
      }
    } catch (error) {
      console.error('Failed to load questionnaire:', error);
    }
  };

  const toggleTheme = () => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  };

  const toggleSection = (sectionIndex: number) => {
    setExpandedSections(prev => {
      if (prev.includes(sectionIndex)) {
        return prev.filter(i => i !== sectionIndex);
      } else {
        return [...prev, sectionIndex];
      }
    });
  };

  // Find which section contains a specific step
  const getSectionForStep = (stepIndex: number): number => {
    return steps.findIndex(section => 
      section.subSteps?.some(subStep => subStep.stepIndex === stepIndex)
    );
  };

  // Check if a conditional section or subsection should be shown
  const shouldShowConditional = (conditional?: string): boolean => {
    if (!conditional || !currentQuestionnaire) return true;
    
    // Check if the condition is met in the form data
    const formData = currentQuestionnaire.formData;
    
    if (conditional === 'needsEcommerce') {
      const needsEcommerce = formData.needsEcommerce;
      return needsEcommerce === true || needsEcommerce === 'true' || formData.projectType === 'ecommerce';
    }
    
    if (conditional === 'true') {
      return true;
    }
    
    if (conditional === 'false') {
      return false;
    }
    
    // Handle Current Website condition
    if (conditional === 'hasWebsite') {
      return !!formData.website && formData.website.trim() !== '';
    }
    
    return true;
  };

  // Get the display name for a questionnaire
  const getQuestionnaireName = (questionnaire: any): string => {
    if (!questionnaire) return 'No Questionnaire Selected';
    
    return questionnaire.formData.company || 
           questionnaire.formData.name || 
           'Untitled Questionnaire';
  };

  // Format the date for display
  const formatDate = (date: any): string => {
    if (!date) return '';
    
    // Check if the date is a Firebase Timestamp (has toDate method)
    if (date.toDate && typeof date.toDate === 'function') {
      date = date.toDate();
    }
    
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(date);
  };

  // Simulate loading state for demonstration purposes
  useEffect(() => {
    // Set loading to false after a short delay to simulate data fetching
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1500);
    
    return () => clearTimeout(timer);
  }, []);

  // Render questionnaire list item skeleton
  const renderQuestionnaireSkeletons = () => (
    <div className="space-y-1">
      {[1, 2, 3].map((i) => (
        <div key={i} className="px-2 py-1.5">
          <div className="flex items-center min-w-0 w-full">
            <Skeleton className="w-2 h-2 rounded-full mr-2" />
            <Skeleton className="h-4 w-3/4" />
          </div>
          <Skeleton className="h-3 w-1/2 ml-4 mt-1" />
        </div>
      ))}
    </div>
  );

  // Update the questionnaire dropdown section
  const renderQuestionnaireDropdown = () => (
    <div className="relative mt-2 px-2 mb-4" ref={questionnaireDropdownRef}>
      <div 
        className="flex items-center justify-between px-3 py-2 text-sm rounded-md border border-gray-200 bg-white cursor-pointer"
        onClick={toggleQuestionnaireDropdown}
      >
        <div className="flex items-center min-w-0 truncate text-gray-900">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
          <span className="truncate text-gray-900 font-medium">{location.pathname.includes('/questionnaire/form') ? getQuestionnaireName(currentQuestionnaire) : 'Select Questionnaire'}</span>
        </div>
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`transition-transform ${questionnaireDropdownOpen ? 'rotate-180' : ''}`}><polyline points="6 9 12 15 18 9"></polyline></svg>
      </div>
      
      {/* Questionnaire dropdown menu */}
      {questionnaireDropdownOpen && (
        <div className="absolute top-full mt-1 left-0 w-full bg-white rounded-md shadow-lg border border-gray-200 py-1 z-10 max-h-60 overflow-y-auto">
          <div className="px-3 py-2">
            <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">Your Questionnaires</h3>
            {/* Render questionnaire list */}
            {loading ? renderQuestionnaireSkeletons() : (
              <div className="space-y-1">
                {userQuestionnaires && userQuestionnaires.length > 0 ? (
                  userQuestionnaires.map((questionnaire) => (
                    <div 
                      key={questionnaire.id} 
                      className={`px-2 py-1.5 rounded-md cursor-pointer hover:bg-gray-100 ${currentQuestionnaire?.id === questionnaire.id ? 'bg-gray-100' : ''}`}
                      onClick={() => handleQuestionnaireSelect(questionnaire.id)}
                    >
                      <div className="flex items-center min-w-0">
                        <span className={`w-2 h-2 rounded-full mr-2 ${questionnaire.isComplete ? 'bg-green-500' : 'bg-amber-500'}`}></span>
                        <span className="truncate text-gray-900 font-medium">
                          {getQuestionnaireName(questionnaire)}
                        </span>
                      </div>
                      <div className="ml-4 text-xs text-gray-500">
                        {formatDate(questionnaire.updatedAt)}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-sm text-gray-500 py-1">No questionnaires found</div>
                )}
              </div>
            )}
            {/* Create new questionnaire button */}
            <div className="mt-2 pt-2 border-t border-gray-100">
              <button
                onClick={() => {
                  navigate('/questionnaire');
                  setQuestionnaireDropdownOpen(false);
                  // Close mobile sidebar if provided
                  if (onCloseMobile) {
                    onCloseMobile();
                  }
                }}
                className="w-full text-left px-2 py-1.5 text-sm text-primary hover:bg-gray-100 rounded-md flex items-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1.5"><path d="M12 5v14M5 12h14"></path></svg>
                Create New Questionnaire
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const shouldShowSteps = () => {
    // Only show steps if we're in the form view and have a current questionnaire
    return location.pathname.includes('/questionnaire/form') && currentQuestionnaire;
  };

  // Close mobile sidebar when navigating to dashboard
  const handleDashboardClick = () => {
    navigate('/questionnaire');
    // Close mobile sidebar if provided
    if (onCloseMobile) {
      onCloseMobile();
    }
  };

  const renderSidebarContent = () => (
    <div className="h-full flex flex-col bg-white border-r border-gray-200 w-64 md:w-72">
      <div className="flex-shrink-0 p-4 flex items-center justify-between">
        <div className="hidden md:flex items-center">
          <a href="/questionnaire" onClick={(e) => {
            e.preventDefault();
            handleDashboardClick();
          }}>
            <img src={logo} alt="Logo" className="h-[3.75rem] w-auto cursor-pointer" />
          </a>
        </div>
        {/* Close button for mobile only */}
        <button 
          className="md:hidden text-gray-500 hover:text-gray-700"
          onClick={onCloseMobile}
          aria-label="Close sidebar"
        >
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            width="20" 
            height="20" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          >
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      </div>
      
      {/* Questionnaire selector */}
      {renderQuestionnaireDropdown()}
      
      {/* Navigation links */}
      <nav className="flex-1 px-2 pb-4 space-y-1 overflow-y-auto">
        {/* Dashboard link */}
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            handleDashboardClick();
          }}
          className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
            isActive('/questionnaire') && !location.pathname.includes('/form')
              ? 'bg-gray-100 text-gray-900'
              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
          }`}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-3 flex-shrink-0"><rect x="3" y="3" width="7" height="7"></rect><rect x="14" y="3" width="7" height="7"></rect><rect x="14" y="14" width="7" height="7"></rect><rect x="3" y="14" width="7" height="7"></rect></svg>
          Dashboard
        </a>
        
        {/* Questionnaire steps */}
        {shouldShowSteps() && (
          <div className="space-y-1 mt-4">
            <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-2 mb-2">
              Questionnaire Steps
            </div>
            
            {/* Render sections and steps */}
            {steps.map((section, sectionIndex) => {
              // Skip conditional sections that don't meet the condition
              if (section.conditional && !shouldShowConditional(section.conditional)) {
                return null;
              }
              
              // Get the first step index for this section
              const firstStepIndex = section.subSteps?.[0]?.stepIndex;
              
              // Check if this section contains the current step
              // Handle both sections with substeps and direct sections
              const isCurrentSection = section.subSteps && section.subSteps.length > 0 
                ? section.subSteps.some(subStep => subStep.stepIndex === currentStep)
                : section.stepIndex === currentStep;
              
              // Check if this section is expanded
              const isExpanded = expandedSections.includes(sectionIndex);
              
              return (
                <div key={`section-${sectionIndex}`} className="space-y-1">
                  {/* Section header */}
                  <button
                    onClick={() => {
                      // If this is a direct section (no substeps), navigate to it
                      if (!section.subSteps || section.subSteps.length === 0) {
                        const isAccessible = currentQuestionnaire && 
                                            currentQuestionnaire.currentStep !== undefined && 
                                            section.stepIndex !== undefined &&
                                            (section.stepIndex <= currentQuestionnaire.currentStep || 
                                             currentQuestionnaire.isComplete);
                        if (isAccessible && section.stepIndex !== undefined) {
                          handleNavigate(section.stepIndex);
                        }
                      } else {
                        // For sections with substeps, toggle expansion
                        toggleSection(sectionIndex);
                      }
                    }}
                    className={`w-full flex items-center justify-between px-2 py-1.5 text-sm font-medium rounded-md ${
                      isCurrentSection
                        ? 'bg-gray-100 text-gray-900'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <div className="flex items-center">
                      <div className={`flex items-center justify-center w-5 h-5 rounded-full mr-2 text-xs
                        ${isCurrentSection ? 'bg-gray-800 text-white' : 
                          // Handle both direct sections and sections with substeps
                          (section.subSteps && section.subSteps.length > 0 
                            ? section.subSteps.some(subStep => 
                                currentQuestionnaire?.currentStep !== undefined && 
                                (subStep.stepIndex <= currentQuestionnaire.currentStep || currentQuestionnaire.isComplete)
                              )
                            : currentQuestionnaire?.currentStep !== undefined && 
                              section.stepIndex !== undefined &&
                              (section.stepIndex <= currentQuestionnaire.currentStep || currentQuestionnaire.isComplete)
                          ) ? 'bg-gray-300 text-black' : 
                          'bg-gray-200 text-gray-500'}`}>
                        {(section.subSteps && section.subSteps.length > 0 
                          ? section.subSteps.some(subStep => 
                              currentQuestionnaire?.currentStep !== undefined && 
                              (subStep.stepIndex <= currentQuestionnaire.currentStep || currentQuestionnaire.isComplete)
                            )
                          : currentQuestionnaire?.currentStep !== undefined && 
                            section.stepIndex !== undefined &&
                            (section.stepIndex <= currentQuestionnaire.currentStep || currentQuestionnaire.isComplete)
                        ) && !isCurrentSection ? (
                          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                        ) : (
                          sectionIndex + 1
                        )}
                      </div>
                      <span>{section.title}</span>
                    </div>
                    {section.subSteps && section.subSteps.length > 0 && (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                      >
                        <polyline points="6 9 12 15 18 9"></polyline>
                      </svg>
                    )}
                  </button>
                  
                  {/* Subsections/steps */}
                  {isExpanded && section.subSteps && (
                    <div className="ml-4 space-y-1">
                      {section.subSteps.map((subStep, subStepIndex) => {
                        // Skip conditional subsections that don't meet the condition
                        if (subStep.conditional && !shouldShowConditional(subStep.conditional)) {
                          return null;
                        }
                        
                        // Determine if this step is accessible (has been visited)
                        // Use a more robust approach that accounts for dynamic step indices
                        const isAccessible = currentQuestionnaire && 
                                            currentQuestionnaire.currentStep !== undefined && 
                                            (subStep.stepIndex <= currentQuestionnaire.currentStep || 
                                             currentQuestionnaire.isComplete);
                        
                        // Determine if this is the current step
                        const isActiveStep = subStep.stepIndex === currentStep;
                        
                        return (
                          <a
                            key={`substep-${sectionIndex}-${subStepIndex}`}
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              if (isAccessible) {
                                handleNavigate(subStep.stepIndex);
                              }
                            }}
                            className={`flex items-center px-2 py-1.5 text-sm rounded-md ${
                              isActiveStep
                                ? 'bg-gray-100 text-gray-900 font-medium'
                                : isAccessible
                                ? 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                : 'text-gray-400 cursor-not-allowed'
                            }`}
                          >
                            <div className={`flex items-center justify-center w-5 h-5 rounded-full mr-2 text-xs
                              ${isActiveStep ? 'bg-gray-800 text-white' : 
                                isAccessible ? 'bg-gray-300 text-black' : 
                                'bg-gray-200 text-gray-500'}`}>
                              {isAccessible && !isActiveStep ? (
                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                              ) : (
                                String.fromCharCode(97 + subStepIndex) // Convert to 'a', 'b', 'c', etc.
                              )}
                            </div>
                            {subStep.title}
                          </a>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
            
            {/* Completion step */}
            {currentQuestionnaire && (
              <div 
                className={`flex items-center px-2 py-1.5 text-sm rounded-md ${
                  isActive('/questionnaire/success') ? 'bg-gray-100 text-gray-900 font-medium' : 
                  currentQuestionnaire.isComplete ? 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 cursor-pointer' : 
                  'text-gray-400 cursor-not-allowed'
                }`}
                onClick={() => {
                  if (currentQuestionnaire.isComplete) {
                    navigate('/questionnaire/success');
                    if (onCloseMobile) {
                      onCloseMobile();
                    }
                  }
                }}
              >
                <div className={`flex items-center justify-center w-5 h-5 rounded-full mr-2 text-xs
                  ${isActive('/questionnaire/success') ? 'bg-gray-800 text-white' : 
                    currentQuestionnaire.isComplete ? 'bg-green-500 text-white' : 
                    'bg-gray-200 text-gray-500'}`}>
                  {currentQuestionnaire.isComplete ? (
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                  ) : (
                    <span>✓</span>
                  )}
                </div>
                Complete
              </div>
            )}
          </div>
        )}
      </nav>
      
      {/* User profile and settings */}
      <div className="flex-shrink-0 flex border-t border-gray-200 p-4">
        <div className="flex-shrink-0 w-full group block">
          <div className="flex items-center">
            <div>
              <button
                onClick={toggleProfile}
                className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-gray-500 text-sm font-medium text-white"
              >
                {currentUser?.email?.charAt(0).toUpperCase() || 'U'}
              </button>
            </div>
            <div className="ml-3 flex-1">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-gray-700 group-hover:text-gray-900 truncate">
                  {currentUser?.email || 'User'}
                </p>
                <div className="relative" ref={profileRef}>
                  <button
                    onClick={toggleProfile}
                    className="ml-2 flex-shrink-0 p-1 rounded-full text-gray-400 hover:text-gray-500"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
                  </button>
                  
                  {/* Profile dropdown */}
                  {profileOpen && (
                    <div className="origin-top-right absolute right-0 bottom-full mb-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
                      <div className="py-1" role="menu" aria-orientation="vertical" aria-labelledby="user-menu">
                        <button
                          onClick={handleLogout}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          role="menuitem"
                        >
                          Sign out
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <p className="text-xs text-gray-500 truncate">
                {currentUser?.email ? 'Logged in' : 'Guest'}
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Settings */}
      <div className="flex-shrink-0 flex border-t border-gray-200 p-2">
        <div className="flex-1 flex justify-between">
          <div className="relative" ref={settingsRef}>
            <button
              onClick={toggleSettings}
              className="group flex items-center px-2 py-2 text-sm font-medium rounded-md text-gray-600 hover:bg-gray-50 hover:text-gray-900"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-3 flex-shrink-0"><circle cx="12" cy="12" r="3"></circle><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path></svg>
              Settings
            </button>
            
            {/* Settings dropdown */}
            {settingsOpen && (
              <div className="origin-bottom-left absolute left-0 bottom-full mb-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
                <div className="py-1" role="menu" aria-orientation="vertical">
                  <button
                    onClick={toggleTheme}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    role="menuitem"
                  >
                    {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return renderSidebarContent();
};

export default QuestionnaireSidebar; 